import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, MaskText } from "./Reveal";

const FAQS = [
  ["What if I haven't started my business yet?", "Perfect. The framework can help you determine what needs to be validated and built before you invest too heavily in an unproven idea."],
  ["What if I already have an established business?", "The framework is designed to be revisited. You can map the whole company or use it for a specific initiative, product, expansion or pivotal decision."],
  ["Is this just for product companies?", "No. Brian's experience comes from building UGG, but the lifecycle framework applies to product, service and other founder-led businesses."],
  ["Do I have to finish it in exactly seven consecutive days?", "The program is structured as a seven-day experience because each day builds on the last. It should be possible to move at your own pace once the evergreen version is live."],
  ["What is the Founder Roadmap Guide?", "It is an AI-supported guide trained around the roadmap methodology. It asks questions about your business and helps adapt the experience to your stage, challenges and priorities."],
  ["Can I use this again later?", "Yes. That is one of the central ideas of the program. The original roadmap is not meant to become permanent. Return when the business changes and run the framework again."],
  ["What happens January 5, 2027?", "The program officially launches. The first 50 purchasers will also receive access to a private live kickoff with Brian Smith."],
];

const Row = ({ q, a, i, open, onToggle }) => (
  <div className="border-b border-[var(--line)]">
    <button data-testid={`faq-${i}`} onClick={onToggle} className="group w-full flex items-start gap-6 py-6 text-left">
      <span className="font-sans text-[11px] tracking-[0.2em] text-cognac pt-2 w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
      <h3 className="flex-1 font-serif text-xl sm:text-2xl text-ocean tracking-tight group-hover:text-cognac transition-colors duration-300">{q}</h3>
      <span className={`font-sans text-2xl text-ocean/50 transition-transform duration-400 ${open ? "rotate-45" : ""}`}>+</span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p data-testid={`faq-answer-${i}`} className="pb-7 pl-12 pr-10 max-w-2xl font-sans text-base text-ink/70 leading-relaxed">{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative bg-cream py-24 sm:py-32">
      <div className="mx-auto max-w-[1300px] px-5 sm:px-8 grid lg:grid-cols-12 gap-y-10 gap-x-12">
        <div className="lg:col-span-4">
          <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">Questions</span>
          <h2 className="mt-6 font-serif text-ocean tracking-[-0.02em] leading-[0.98] text-4xl sm:text-5xl">
            <MaskText lines={["Before you", <span key="s" className="italic text-cognac">start.</span>]} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 font-sans text-base text-ink/65 leading-relaxed max-w-xs">
              Still weighing it up? These are the questions founders ask most.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-8 border-t border-[var(--line)]">
          {FAQS.map(([q, a], i) => (
            <Row key={q} q={q} a={a} i={i} open={open === i} onToggle={() => setOpen(open === i ? null : i)} />
          ))}
        </div>
      </div>
    </section>
  );
};
